import { Injectable } from '@angular/core';
import { Adapter } from '@core/adapter';
import {
    LiveAssetStatus,
    LiveAssetStatusAlert,
    LiveStatusState,
} from '@app/shared/models/live-status.model';
import { STATUS_INDICATOR } from '@app/shared/enum/status';
import { NOT_CONFIGURED_STATE } from '@app/features/dashboard/constants/live-status';
import moment from 'moment';
import { AssetAdapter } from '@core/services/adapters/asset.adapter';
import { DateService } from '@app/shared/helpers/date.service';
import { DATE_FORMAT } from '@app/shared/enum/dates';

@Injectable()
export class AssetStatusAdapter implements Adapter<LiveAssetStatus> {
    constructor(private assetAdapter: AssetAdapter, private dateService: DateService) {}

    adapt(data: any): LiveAssetStatus {
        const alerts: LiveAssetStatusAlert[] = data.alerts
            ? data.alerts.map((alert: any) => this.adaptAlert(alert))
            : [];

        const state: LiveStatusState = {
            id: null,
            code: data.state || NOT_CONFIGURED_STATE.code,
            description: data.stateDescription || NOT_CONFIGURED_STATE.description,
            group: null,
        };

        const result = {
            asset: this.assetAdapter.adapt(data),
            hasCategories: data.hasCategories || false,
            startTime: data.startTime ? moment(data.startTime) : moment(),
            totalAlerts: data.totalAlerts || 0,
            exploreGroups: data.groups && data.groups.length ? data.groups : [],
            state,
            alerts,
        };

        return {
            ...result,
            elapsedTime: result.state.description
                ? `${result.state.description} for the last ${this.dateService.getDuration(
                      moment().diff(result.startTime),
                      true,
                      false,
                      2
                  )}.`
                : '',
        };
    }

    private adaptAlert(data: any) {
        const result: Partial<LiveAssetStatusAlert> = {
            id: data.id || 0,
            name: data.name || '',
            active: data.active || false,
            severity: data.severity || STATUS_INDICATOR.WARNING,
            startTime: data.startTime ? moment(data.startTime) : moment(),
            stopTime: data.stopTime ? moment(data.stopTime) : null,
        };

        let elapsedTime = `${result.name} alert active for last ${this.dateService.getDuration(
            moment().diff(result.startTime),
            true,
            false,
            2
        )}.`;

        if (result.stopTime) {
            elapsedTime = `${result.name} alert ${this.dateService.getFormattedDate(
                result.startTime.valueOf(),
                DATE_FORMAT.SHORT_DATE_TIME
            )} to ${result.stopTime.format(
                result.stopTime.diff(result.startTime, 'days') > 1
                    ? this.dateService.getFormattedDate(
                          result.stopTime.valueOf(),
                          DATE_FORMAT.SHORT_DATE_TIME
                      )
                    : this.dateService.getFormattedDate(result.stopTime.valueOf(), DATE_FORMAT.TIME)
            )}`;
        }

        return {
            ...result,
            elapsedTime,
        };
    }
}
